// ── Unit system module ───────────────────────────────────────────────
// User-pref-aware unit conversion. Authoritative source: the signed-in
// user's `preferences.units` ('metric' | 'imperial'), surfaced via
// `window.__USER_UNITS` on hydrated pages. Anonymous visitors fall back
// to localStorage (`smartpluvia-units`), then to 'metric'.
//
// DB stores metric by default (L/min, bar, meters).
//
// PHASE 1 of issue #1431 — per-callsite refactor: the format* helpers
// now accept an optional `units` argument. When a callsite knows its
// own units (e.g. the planner state.userUnits captured at boot), it
// should pass them explicitly; otherwise the global `getUnits()` lookup
// is used to preserve backward compatibility for ad-hoc calls.

export type UnitSystem = 'metric' | 'imperial';

const STORAGE_KEY = 'smartpluvia-units';

declare global {
  interface Window {
    __USER_UNITS?: UnitSystem;
  }
}

/** Read the active unit system, in priority order:
 *   1. window.__USER_UNITS — set by `setUnits`, i.e. the toggle, this session
 *   2. `<meta name="user-units">` — what the server rendered the page's unit
 *      captions with (`UnitsHelper#current_units`: the signed-in user's
 *      preference, else the anonymous cookie)
 *   3. localStorage[STORAGE_KEY] — anonymous fallback for a page with no meta
 *   4. 'metric' (SI default)
 *
 * The meta tag matters: `window.__USER_UNITS` was documented as "set by server
 * on hydration" but nothing ever assigned it, so a signed-in user with
 * `units: imperial` got imperial captions from Rails and metric numbers from
 * JS — L/min printed under "GPM", metres under "ft".
 */
export function getUnits(): UnitSystem {
  if (typeof window !== 'undefined' && window.__USER_UNITS) {
    return window.__USER_UNITS === 'imperial' ? 'imperial' : 'metric';
  }
  if (typeof document !== 'undefined') {
    const fromServer = document
      .querySelector<HTMLMetaElement>('meta[name="user-units"]')?.content;
    if (fromServer === 'imperial' || fromServer === 'metric') return fromServer;
  }
  if (typeof localStorage !== 'undefined') {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'imperial' || stored === 'metric') return stored;
    } catch {
      /* localStorage may be blocked — fall through */
    }
  }
  return 'metric';
}

/** Persist the user's unit choice for anonymous sessions / fast UI toggle. */
export function setUnits(u: UnitSystem): void {
  if (typeof window !== 'undefined') window.__USER_UNITS = u;
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, u);
    } catch {
      /* ignore quota / privacy errors */
    }
  }
}

// ── Formatting (mirrors Units::Formatter on the backend) ────────────

const M_TO_FT = 3.2808;
const M2_TO_FT2 = 10.7639;
const BAR_TO_PSI = 14.504;
const LPM_TO_GPM = 0.2642;
const M3H_TO_GPM = 4.4029;
const MM_TO_IN = 0.03937;
const HA_THRESHOLD_M2 = 10_000;
const AC_THRESHOLD_FT2 = 43_560;

/**
 * Resolve the unit system: explicit argument wins, otherwise read from
 * the global `getUnits()` lookup. Keeps callsites ergonomic — pass
 * `units` when known (planner state), omit for one-off scripts.
 */
function resolveUnits(units?: UnitSystem): UnitSystem {
  return units ?? getUnits();
}

/** Format an area in m² as a localized string. Auto-promotes to ha / ac. */
export function formatArea(m2: number, units?: UnitSystem): string {
  const u = resolveUnits(units);
  if (u === 'imperial') {
    const ft2 = m2 * M2_TO_FT2;
    if (ft2 >= AC_THRESHOLD_FT2) {
      return `${(ft2 / AC_THRESHOLD_FT2).toFixed(2)} ac`;
    }
    return `${ft2.toFixed(2)} ft²`;
  }
  if (m2 >= HA_THRESHOLD_M2) {
    return `${(m2 / HA_THRESHOLD_M2).toFixed(2)} ha`;
  }
  return `${m2.toFixed(2)} m²`;
}

/** Format a length in meters as a localized string (m or ft). */
export function formatLength(m: number, units?: UnitSystem): string {
  const u = resolveUnits(units);
  return u === 'imperial'
    ? `${(m * M_TO_FT).toFixed(2)} ft`
    : `${m.toFixed(2)} m`;
}

/** Format a sprinkler / nozzle radius in meters (m or ft, 1 decimal). */
export function formatRadius(m: number, units?: UnitSystem): string {
  const u = resolveUnits(units);
  return u === 'imperial'
    ? `${(m * M_TO_FT).toFixed(1)} ft`
    : `${m.toFixed(1)} m`;
}

/** Format pressure in bar as a localized string (bar or psi). */
export function formatPressure(bar: number, units?: UnitSystem): string {
  const u = resolveUnits(units);
  return u === 'imperial'
    ? `${(bar * BAR_TO_PSI).toFixed(2)} psi`
    : `${bar.toFixed(2)} bar`;
}

/** Format flow in L/min as a localized string (L/min or GPM). */
export function formatFlow(lpm: number, units?: UnitSystem): string {
  const u = resolveUnits(units);
  return u === 'imperial'
    ? `${(lpm * LPM_TO_GPM).toFixed(2)} GPM`
    : `${lpm.toFixed(2)} L/min`;
}

/**
 * Format flow expressed in m³/h (project-wide totals shown by
 * `updateStats`). Imperial users see GPM; metric users see m³/h.
 */
export function formatFlowM3h(m3h: number, units?: UnitSystem): string {
  const u = resolveUnits(units);
  return u === 'imperial'
    ? `${(m3h * M3H_TO_GPM).toFixed(2)} GPM`
    : `${m3h.toFixed(2)} m³/h`;
}

/** Format a small length in mm as mm or in. */
export function formatSmallLength(mm: number, units?: UnitSystem): string {
  const u = resolveUnits(units);
  return u === 'imperial'
    ? `${(mm * MM_TO_IN).toFixed(2)} in`
    : `${mm.toFixed(0)} mm`;
}

/** Format precipitation rate (mm/h vs in/h). */
export function formatPrecipRate(mmh: number, units?: UnitSystem): string {
  const u = resolveUnits(units);
  return u === 'imperial'
    ? `${(mmh * MM_TO_IN).toFixed(2)} in/h`
    : `${mmh.toFixed(1)} mm/h`;
}

// ── Conversion functions (metric → imperial) ────────────────────────

export function convertFlow(lpm: number, system: UnitSystem): number {
  return system === 'imperial' ? lpm * 0.2642 : lpm; // L/min → GPM
}

export function convertLength(m: number, system: UnitSystem): number {
  return system === 'imperial' ? m * 3.2808 : m; // m → ft
}

export function convertSmallLength(mm: number, system: UnitSystem): number {
  return system === 'imperial' ? mm * 0.03937 : mm; // mm → inches
}

export function convertArea(m2: number, system: UnitSystem): number {
  return system === 'imperial' ? m2 * 10.764 : m2; // m² → ft²
}

export function convertPrecipRate(mmh: number, system: UnitSystem): number {
  return system === 'imperial' ? mmh * 0.03937 : mmh; // mm/h → in/h
}

// ── Formatting ──────────────────────────────────────────────────────

export function formatUnit(value: number, decimals: number = 1): string {
  return value.toFixed(decimals);
}

// ── Editable fields ─────────────────────────────────────────────────
// State holds SI (metres, millimetres, L/min, bar). A numeric field converts
// at its boundary, so an imperial user reads and types feet, inches, GPM
// and psi.

export type FieldQuantity = 'length' | 'smallLength' | 'flow' | 'pressure';

const FIELD_FACTORS: Record<FieldQuantity, number> = { length: M_TO_FT, smallLength: MM_TO_IN, flow: LPM_TO_GPM, pressure: BAR_TO_PSI };
const IMPERIAL_FIELD_DIGITS: Record<FieldQuantity, number> = { length: 1, smallLength: 2, flow: 2, pressure: 2 };

/** An SI value in the user's units, unrounded. */
export function toFieldUnits(si: number, quantity: FieldQuantity, units: UnitSystem): number {
  return units === 'imperial' ? si * FIELD_FACTORS[quantity] : si;
}

/**
 * The text a numeric field shows for an SI value. Metric keeps the value as
 * the field always showed it (`metricDigits`, or as it is); imperial converts.
 */
export function toFieldText(si: number, quantity: FieldQuantity, units: UnitSystem, metricDigits?: number): string {
  if (units === 'imperial') return toFieldUnits(si, quantity, units).toFixed(IMPERIAL_FIELD_DIGITS[quantity]);
  return metricDigits === undefined ? String(si) : si.toFixed(metricDigits);
}

/** A number typed into a field in the user's units, back in SI. NaN stays NaN. */
export function fromFieldValue(value: number, quantity: FieldQuantity, units: UnitSystem): number {
  return units === 'imperial' ? value / FIELD_FACTORS[quantity] : value;
}

/**
 * An SI bound (a field's `min` or `max`) in the user's units, rounded outward
 * onto the field's step so every value the field shows stays inside it.
 */
export function toFieldBound(si: number, quantity: FieldQuantity, units: UnitSystem, step: number, bound: 'min' | 'max'): number {
  const value = toFieldUnits(si, quantity, units);
  const steps = bound === 'min' ? Math.floor(value / step) : Math.ceil(value / step);
  const decimals = (String(step).split('.')[1] || '').length;
  return Number((steps * step).toFixed(decimals));
}

// ── Unit labels (from i18n locale files) ────────────────────────────

import { t } from './i18n.ts';

export function getUnitLabels(system: UnitSystem) {
  if (system === 'imperial') {
    return {
      flow: t('units.gpm'), pressure: t('units.psi'), length: t('units.ft'),
      smallLength: t('units.in'), area: t('units.ft2'), precipRate: t('units.inh'),
      diameter: t('units.in'), flowVolume: t('units.gpm'),
    };
  }
  return {
    flow: t('units.lpm'), pressure: t('units.bar'), length: t('units.m'),
    smallLength: t('units.mm'), area: t('units.m2'), precipRate: t('units.mmh'),
    diameter: t('units.mm'), flowVolume: t('units.m3h'),
  };
}
